<template>
    <div ref="root">
        <slot></slot>
    </div>
</template>

<script lang="ts" setup>
import {
  onMounted,
  ref,
} from 'vue';

const props = withDefaults(defineProps<{
  threshold?: number
  clearOnEnter?: boolean
}>(), {
  threshold: 0,
  clearOnEnter: true,
});

const emit = defineEmits<{
  observeenter: [element?: HTMLElement],
  observeleave: [element?: HTMLElement],
  resize: [dimensions: { height?: number, width?: number }],
}>();

const root = ref<HTMLElement>();
const rootSize = ref<{
  height?: number
  width?: number
}>({
  width: undefined,
  height: undefined,
});

const setRootSize = (): void => {
  rootSize.value.width = root.value?.offsetWidth;
  rootSize.value.height = root.value?.offsetHeight;

  emit('resize', rootSize.value);
};

const handleIntersect = (
  entries: IntersectionObserverEntry[],
  observer: IntersectionObserver,
): void => {
  let entered = false;

  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      entered = true;
    }
  });

  if (entered) {
    emit('observeenter', root.value);
    if (props.clearOnEnter && root.value) {
      observer.unobserve(root.value);
    }
  } else {
    emit('observeleave', root.value);
  }
};

const createObserver = (): void => {
  const observer = new IntersectionObserver(handleIntersect, {
    root: null,
    threshold: props.threshold,
  });

  const resizeObserver = new ResizeObserver(setRootSize);

  if (root.value) {
    observer.observe(root.value);
    resizeObserver.observe(root.value);
  }
};

onMounted(() => {
  if (window.IntersectionObserver) {
    createObserver();
  } else {
    emit('observeenter', root.value);
  }

  setRootSize();
});

defineExpose({
  setRootSize,
});
</script>

<style lang="scss">
    .io-lazy-background {
        position: relative;
        overflow: hidden;

        > div {
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            width: 100%;
            transform-origin: center;
            transition: transform .7s ease-in-out;
        }
    }
</style>
